import type { AiChatResponse, AiChatTraceStep, QuickQuestionTrace } from './api';

/** Everything a reader needs to diagnose one answer, as one JSON file that
 *  also carries a transcript in plain words (79.56). */
export interface TraceExport {
  format: 'tres-trace/1';
  exported_at: string;
  question: string;
  database: string;
  graph: string | null;
  asked_at: string | null;
  asked_by: string | null;
  model: string | null;
  route: string | null;
  clarifications: string | null;
  answer: string;
  stop_reason: string | null;
  latency_ms: number | null;
  tokens: {
    input: number | null;
    output: number | null;
    cache_read: number | null;
    cache_write: number | null;
    model_calls: number | null;
  };
  tokenomics: unknown;
  steps: AiChatTraceStep[];
  transcript: string;
}

function routeAndClarifications(steps: AiChatTraceStep[]): {
  route: string | null;
  clarifications: string | null;
} {
  let route: string | null = null;
  let clarifications: string | null = null;
  for (const step of steps) {
    if (step.kind !== 'tool_result') continue;
    if (step.tool === 'router' && route === null) route = step.text;
    if (step.tool === 'clarifications' && clarifications === null) clarifications = step.text;
  }
  return { route, clarifications };
}

function stepText(step: AiChatTraceStep, n: number): string {
  switch (step.kind) {
    case 'generated_query':
      return `${n}. Query\n${step.sparql}`;
    case 'query_result':
      return `${n}. Result: ${step.rows.length} row${step.rows.length === 1 ? '' : 's'}${
        step.truncated ? ' (cut short)' : ''
      }\n${JSON.stringify(step.rows, null, 2)}`;
    case 'query_error':
      return `${n}. Query failed: ${step.message}\n${step.sparql}`;
    case 'tool_result':
      return `${n}. ${step.tool}\n${step.text}`;
  }
}

function transcriptOf(e: Omit<TraceExport, 'transcript' | 'format' | 'exported_at'>): string {
  const lines = [
    `Question: ${e.question}`,
    `Database: ${e.database}${e.graph ? `, graph ${e.graph}` : ''}`,
    e.asked_at ? `Asked: ${e.asked_at}${e.asked_by ? ` by ${e.asked_by}` : ''}` : null,
    e.model ? `Model: ${e.model}` : null,
    `Route: ${e.route ?? 'not recorded'}`,
    `Clarifications in force: ${e.clarifications ?? 'none recorded'}`,
    '',
    'Steps',
    ...e.steps.map((s, i) => stepText(s, i + 1)),
    '',
    'Answer',
    e.answer || '(no answer)',
    '',
    `Stopped because: ${e.stop_reason ?? 'not recorded'}`,
    e.latency_ms != null ? `Took: ${e.latency_ms} ms` : null,
    `Tokens: ${e.tokens.input ?? '?'} in, ${e.tokens.output ?? '?'} out, ${e.tokens.cache_read ?? 0} cache read, ${e.tokens.cache_write ?? 0} cache written, ${e.tokens.model_calls ?? '?'} model call(s)`,
  ];
  return lines.filter((l) => l !== null).join('\n');
}

function finish(e: Omit<TraceExport, 'transcript' | 'format' | 'exported_at'>): TraceExport {
  return { format: 'tres-trace/1', exported_at: new Date().toISOString(), ...e, transcript: transcriptOf(e) };
}

/** The export of the answer just given on the page. */
export function exportOfAnswer(input: {
  question: string;
  database: string;
  graph: string | null;
  model: string | null;
  response: AiChatResponse;
}): TraceExport {
  const r = input.response;
  const { route, clarifications } = routeAndClarifications(r.trace);
  return finish({
    question: input.question,
    database: input.database,
    graph: input.graph,
    asked_at: null,
    asked_by: null,
    model: input.model,
    route,
    clarifications,
    answer: r.answer,
    stop_reason: r.stop_reason,
    latency_ms: r.latency_ms,
    tokens: {
      input: r.usage?.input_tokens ?? null,
      output: r.usage?.output_tokens ?? null,
      cache_read: r.usage?.cache_read_input_tokens ?? null,
      cache_write: r.usage?.cache_creation_input_tokens ?? null,
      model_calls: r.llm_calls,
    },
    tokenomics: { trips: r.trips, turn_token_budget: r.turn_token_budget ?? null, max_roundtrips: r.max_roundtrips },
    steps: r.trace,
  });
}

/** The export of a question asked earlier, from its record. */
export function exportOfRecord(row: QuickQuestionTrace, databaseName: string): TraceExport {
  const { route, clarifications } = routeAndClarifications(row.trace);
  let tokenomics: unknown = null;
  try {
    tokenomics = row.tokenomics_json ? JSON.parse(row.tokenomics_json) : null;
  } catch {
    tokenomics = null;
  }
  return finish({
    question: row.question_text,
    database: databaseName,
    graph: row.graph,
    asked_at: row.asked_at,
    asked_by: row.asked_by_name,
    model: row.model_provider ? `${row.model_provider}:${row.model_name ?? ''}` : null,
    route,
    clarifications,
    answer: row.answer_text,
    stop_reason: row.stop_reason,
    latency_ms: row.latency_ms,
    tokens: {
      input: row.input_tokens,
      output: row.output_tokens,
      cache_read: row.cache_read_tokens,
      cache_write: row.cache_write_tokens,
      model_calls: row.llm_calls,
    },
    tokenomics,
    steps: row.trace,
  });
}

/** The file name: the question's first words, the database and the moment. */
export function traceFileName(e: TraceExport): string {
  const words = e.question
    .split(/\s+/)
    .slice(0, 6)
    .join('-')
    .replace(/[^\p{L}\p{N}_-]+/gu, '')
    .slice(0, 48);
  const when = (e.asked_at ?? e.exported_at).replace(/[:.]/g, '-').slice(0, 19);
  return `trace-${e.database}-${words || 'question'}-${when}.json`;
}

export function downloadTraceExport(e: TraceExport): void {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(e, null, 2)], { type: 'application/json' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = traceFileName(e);
  a.click();
  URL.revokeObjectURL(url);
}
